import * as members from '../../privates/privateMembers';
import {setAsyncFunctions} from '../../privates/utils/asyncDecorator';

function changeElementCollapsiblity(RMI, compId, isCollapsed, cb) {
    RMI.setProps(compId, { isCollapsed }, cb);
}
/**
 * @mixin CollapsedMixin
 * @summary Provides functionality for elements that can be collapsed.
 *
 *  To learn about the behavior of a collapsed element,
 *  see the [`collapsed`](#collapsed) property.
 * @memberof $w
 */
class CollapsedMixin {

    /**
     * @function collapse
     * @syntax
     * function collapse(): Promise<void>
     * @summary Collapses the element and sets its `collapsed` property to `true`.
     * @description
     *  The `collapse()` function returns a Promise that is resolved when the
     *  element's [`collapsed`](#collapsed) property has been set to `true`.
     *
     *  To learn about the behavior of a collapsed element,
     *  see the [`collapsed`](#collapsed) property.
     *
     *  You can also collapse an element
     *  when the page loads by using the Properties panel in the Editor.
     * @returns {Promise}
     * @fulfill {void} When the element's `collapsed` property has been set to `true`.
     * @snippet [CollapsedMixin-collapse.es6=Collapse an element]
     * @snippet [CollapsedMixin-collapse_then.es6=Collapse an element and log a message when done]
     * @snippet [CollapsedMixin_toggle_collapsed.es6=Toggle an element's collapsed state]
     * @see [expand( )](#expand)
     * @see [collapsed](#collapsed)
     * @see [hide( )]($w.HiddenMixin.html#hide)
     * @memberof $w.CollapsedMixin
     */
    collapse() {
        return new Promise((resolve) => {
            const RMI = members._RMI.get(this);
            const compId = members._compId.get(this);

            if (!this.collapsed){
                changeElementCollapsiblity(RMI, compId, true, resolve);
            } else {
                resolve();
            }
        });
    }

    /**
     * @member collapsed
     * @label property
     * @syntax
     * get collapsed(): boolean
     * @summary Indicates if the element is collapsed or expanded.
     * @description
     *  If `collapsed` is `true`, the element is not displayed on the page under
     *  any circumstances. A collapsed element, unlike a
     *  [`hidden`]($w.HiddenMixin.html#hide) element, does not take up any
     *  space on the page. When collapsed, elements positioned within 70 pixels
     *  below the collapsed element and each other move up to take the
     *  collapsed element's place where possible. The elements that move up
     *  maintain their positions relative to one another.
     *
     *  If `collapsed` is `false`, the element _may_ be displayed on the page.
     *  Elements that moved up to take the collapsed element's place on the
     *  page are moved back down.
     *
     *  However, an expanded element (an element whose `collapsed` property is
     *  `false`) is still not displayed if:
     *
     *  + It is [hidden]($w.HiddenMixin.html#hidden).
     *  + Its [parent element]($w.Node.html#parent) is hidden or collapsed.
     *  + It is a slide in a [Slideshow]($w.Slideshow.html) which is
     *    currently not being displayed.
     *
     *
     *  Even if the element is not be displayed due to the conditions
     *  mentioned above, if its `collapsed` property is `false`, it's
     *  displayed when the conditions no longer apply.
     *
     *  To set the `collapsed` property on an element, use the element's
     *  [`collapse()`](#collapse) and [`expand()`](#expand) functions.
     *
     *  If you select **Collapsed on load** in the element's Properties panel
     *  in the Editor, the `collapsed` property is set to `true` when the page loads.
     * @snippet [CollapsedMixin-collapsed.es6=Get an element's collapsed status]
     * @snippet [CollapsedMixin_toggle_collapsed.es6=Toggle an element's collapsed state]
     * @type {external:Boolean}
     * @default false
     * @see [collapse( )](#collapse)
     * @see [expand( )](#expand)
     * @see [hide( )]($w.HiddenMixin.html#hide)
     * @see [show( )]($w.HiddenMixin.html#show)
     * @see [hidden]($w.HiddenMixin.html#hidden)
     * @memberof $w.CollapsedMixin
     * @attr {external:Boolean} collapsed
     * @readonly
     */
    get collapsed() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const props = RMI.getProps(compId);

        return Boolean(props && props.isCollapsed);
    }

    /**
     * @function expand
     * @syntax
     * function expand(): Promise<void>
     * @summary Expands the element and sets its `collapsed` property to `false`.
     * @description
     *  The `expand()` function returns a Promise that is resolved when the
     *  element's [`collapsed`](#collapsed) property has been set to `false`.
     *
     *  To learn about the behavior of an expanded element,
     *  see the [`collapsed`](#collapsed) property.
     *
     * @returns {Promise}
     * @fulfill {void} When the element's `collapsed` property has been set to `false`.
     * @snippet [CollapsedMixin-expand.es6=Expand an element]
     * @snippet [CollapsedMixin-expand_then.es6=Expand an element and log a message when done]
     * @snippet [CollapsedMixin_toggle_collapsed.es6=Toggle an element's collapsed state]
     * @see [expand( )](#expand)
     * @see [collapsed](#collapsed)
     * @see [show( )]($w.HiddenMixin.html#show)
     * @memberof $w.CollapsedMixin
     */
    expand() {
        return new Promise((resolve) => {
            const RMI = members._RMI.get(this);
            const compId = members._compId.get(this);

            if (this.collapsed){
                changeElementCollapsiblity(RMI, compId, false, resolve);
            } else {
                resolve();
            }
        });
    }
}

setAsyncFunctions(['expand', 'collapse']);

export default CollapsedMixin;
